import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { useConversations } from './hooks/useConversations';
import { ConversationList } from './components/ConversationList';
import { ChatPanel } from './components/ChatPanel';
import { CustomerProfilePanel } from './components/CustomerProfilePanel';
import { EmptyConversationState } from './components/EmptyStates';
import { ConversationsSkeleton } from './components/ConversationsSkeleton';
import { PANEL_FADE } from './lib/motion';

const ConversationsPage = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');

  // Deep-link support: /conversations?open=<id> (e.g. from a magic link) opens
  // that thread directly.
  useEffect(() => {
    const open = searchParams.get('open');
    if (open) setSelectedConversation(open);
  }, [searchParams]);
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { conversations, isLoading } = useConversations({
    search: searchTerm,
    platform: platformFilter !== 'all' ? platformFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  if (isLoading) {
    return <ConversationsSkeleton />;
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-3 sm:p-4 md:p-5 flex flex-col min-h-[calc(100vh-4rem)]">
        <div className="flex-1 flex overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelect={setSelectedConversation}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            platformFilter={platformFilter}
            onPlatformFilterChange={setPlatformFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />

          {/* Center Panel - Chat Interface */}
          <div className="flex flex-1 flex-col bg-slate-50/30 dark:bg-slate-925/40 overflow-hidden">
            {selectedConversation ? (
              <motion.div
                key={selectedConversation}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <ChatPanel conversationId={selectedConversation} />
              </motion.div>
            ) : (
              <EmptyConversationState />
            )}
          </div>

          {/* Right Panel - Customer Profile */}
          <AnimatePresence>
            {selectedConversation && (
              <motion.div
                key="profile-panel"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className="w-80 shrink-0 overflow-y-auto border-l border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900"
              >
                <CustomerProfilePanel conversationId={selectedConversation} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </MotionConfig>
  );
};

export default ConversationsPage;
